import { useId, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { authClient } from './authClient';
import { passwordChecks } from './authForm';
import {
  Footer,
  FormError,
  FormStack,
  PageHeading,
  PasswordChecklist,
  PasswordField,
  PrimaryLink,
  RouterLink,
  SubmitButton,
} from './authUi';
import { ACCOUNT_PATHS } from './paths';

/**
 * Choose a new password, from the link in the reset email.
 *
 * The token arrives in the query string, because that is the only thing an
 * email link can carry. No token means the link was mangled — a mail client
 * wrapping it across two lines is the usual culprit — and the page says how to
 * get a working one rather than showing a form that is certain to fail.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [done, setDone] = useState(false);
  const checklistId = useId();

  if (!token) {
    return (
      <>
        <PageHeading
          title="This link isn't working"
          lede="Reset links work for one hour and only once. Ask for a new one and use the most recent email."
        />
        <PrimaryLink to={ACCOUNT_PATHS.forgotPassword}>Send a new link</PrimaryLink>
        <Footer>
          <RouterLink to={ACCOUNT_PATHS.logIn}>Back to log in</RouterLink>
        </Footer>
      </>
    );
  }

  if (done) {
    return (
      <>
        <PageHeading
          title="Password updated"
          lede="Log in with your new password. Any other device you were logged in on will ask for it too."
        />
        <PrimaryLink to={ACCOUNT_PATHS.logIn}>Log in</PrimaryLink>
      </>
    );
  }

  // No email on this page to compare against, so length is the only rule.
  const checks = passwordChecks(password);
  const passwordOk = checks.every((check) => check.met);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    setFailure('');
    if (!passwordOk) return;

    setBusy(true);
    const result = await authClient.resetPassword(token, password);
    setBusy(false);
    if (!result.ok) {
      setFailure(result.message);
      return;
    }
    setDone(true);
  };

  return (
    <>
      <PageHeading title="Choose a new password" lede="You'll use it to log in on every device." />

      <FormStack onSubmit={submit} noValidate>
        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          describedBy={checklistId}
          error={attempted && !passwordOk ? 'Choose a password that meets this' : ''}
        />
        <PasswordChecklist id={checklistId} checks={checks} />

        <FormError message={failure} />
        <SubmitButton busy={busy} busyLabel="Updating…">
          Update password
        </SubmitButton>
      </FormStack>

      <Footer>
        <RouterLink to={ACCOUNT_PATHS.logIn}>Back to log in</RouterLink>
      </Footer>
    </>
  );
}
